import {
  FormControl,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  FC,
  FocusEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useDynamicFormsTranslate, useRtl } from "@ryde/redux";
import {
  currencySignSelector,
  CustomValidate,
  getRegisterAttributes,
  ICustomField,
  inputValueAsNumberOrEmptyString,
  Log,
  ValidationHelpers,
} from "@ryde/shared";

import { FullWidthTextFieldWrapper } from "../../parts-styled";
import {
  areAdditionsEqual,
  emptyAdditionValue,
  IAdditionValue,
  isAdditionValue,
} from "./addition-value.utils";
import { AdditionSwitch } from "./additions-switch";

export const AdditionValueTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
  formValidation,
  e2eData = "addition-value-template",
}) => {
  const { t } = useTranslation();
  const { isRtl } = useRtl();

  const { control, setValue, setError, getValues } = useFormContext();
  const { disabled } = getRegisterAttributes(formAttributes);

  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });

  const currencySign = useRecoilValue(currencySignSelector);

  const formValue: IAdditionValue | undefined = getValues(name);
  const [addition, setAddition] = useState<IAdditionValue | null>(
    formValue !== undefined && isAdditionValue(formValue)
      ? formValue
      : emptyAdditionValue,
  );
  useEffect(() => {
    if (
      formValue &&
      isAdditionValue(formValue) &&
      !areAdditionsEqual(formValue, addition)
    ) {
      setAddition(formValue);
    } else if (!formValue) {
      setAddition(emptyAdditionValue);
    } else if (formValue && !isAdditionValue(formValue)) {
      Log.warn("Invalid addition format", formValue);
    }
  }, [addition, formValue]);

  const onChangeSwitchValue = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ): void => {
    const newAddition: IAdditionValue = {
      ...addition,
      isCurrency: checked,
    };
    setAddition(newAddition);
    setValue(name, newAddition, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const setTargetValue = (value: string): void => {
    try {
      if (formAttributes?.intRounding) {
        value = parseInt(value, 10).toString();
      }

      const newFieldValue = inputValueAsNumberOrEmptyString(value);
      const newAddition: IAdditionValue = {
        ...addition,
        value: newFieldValue,
      };
      setAddition(newAddition);
      setValue(name, newAddition, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } catch (error) {
      Log.debug("error setting value for numeric field ", error);
      setError(name, { message: t("errors.invalidNumber") });
    }
  };

  const onChangeNumeric = (e: ChangeEvent<HTMLInputElement>): void => {
    setTargetValue(e.target.value);
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>): void => {
    setTargetValue(e.target.value);
  };

  const isSpecificAdditionRequiredRule = Boolean(
    formValidation?.rules.find(rule => {
      return rule.params?.find(param => {
        return (
          typeof param === "object" &&
          "customValidationName" in param &&
          param?.customValidationName ===
            CustomValidate.ADDITIONS_VALUE_IS_REQUIRED
        );
      });
    }),
  );

  const requiredComputed = useMemo(() => {
    return (
      ValidationHelpers.hasRequiredRule(formValidation) ||
      isSpecificAdditionRequiredRule
    );
  }, [formValidation, isSpecificAdditionRequiredRule]);

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, ...restFieldProps },
        fieldState: { error },
      }) => {
        return (
          <FullWidthTextFieldWrapper>
            <FormControl
              required={requiredComputed}
              data-e2e-template="addition-value-template"
              data-e2e={e2eData}
              data-e2e-field-name={name}
              error={!!error}
            >
              <TextField
                {...restFieldProps}
                value={addition?.value ?? ""}
                onBlur={onBlur}
                onChange={onChangeNumeric}
                label={formLabelTranslated}
                required={requiredComputed}
                disabled={disabled}
                error={!!error}
                fullWidth
                type="number"
                data-e2e-template="addition-value-template-text"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="start">
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center" }}
                        >
                          <Typography>%</Typography>
                          <AdditionSwitch
                            checked={addition?.isCurrency}
                            onChange={onChangeSwitchValue}
                          />
                          <Typography>{currencySign}</Typography>
                        </Stack>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    style: {
                      textAlign: isRtl ? "right" : "left",
                      unicodeBidi: "plaintext",
                    },
                  },
                }}
              />
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          </FullWidthTextFieldWrapper>
        );
      }}
    />
  );
};
